import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Table, Tag, Typography, Row, Col, Statistic, Spin, Progress, Button, Tabs, Modal, Form, Input, InputNumber, DatePicker, Select, message, Popconfirm, Divider } from 'antd';
import { HomeOutlined, SoundOutlined, CheckCircleOutlined, BarChartOutlined, PrinterOutlined, DollarOutlined, PlusOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../utils/api';
import { useDeepLinkWhen } from '../utils/deeplink';

const { Title, Text } = Typography;
const studioColors = ['#667eea', '#764ba2', '#f5576c', '#4facfe', '#43e97b', '#fa709a', '#fee140', '#30cfd0'];

const { RangePicker } = DatePicker;

function ReportTab() {
  const [data, setData] = useState({ metadata: [] });
  const [loading, setLoading] = useState(false);
  // AI answers open this page on the asked month (?month=YYYY-MM).
  const [monthFilter, setMonthFilter] = useState(useDeepLinkWhen().month);

  const fetchReport = useCallback(() => {
    setLoading(true);
    const params = {};
    if (monthFilter) {
      params.from = monthFilter.startOf('month').format('YYYY-MM-DD');
      params.to = monthFilter.endOf('month').format('YYYY-MM-DD');
    }
    api.get('/performance/studio-report', { params }).then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false));
  }, [monthFilter]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const studios = data.metadata || [];
  const totalSongs = studios.reduce((s, r) => s + parseInt(r.total_songs || 0), 0);
  const totalDist = studios.reduce((s, r) => s + parseInt(r.distributed || 0), 0);
  const totalPending = studios.reduce((s, r) => s + parseInt(r.pending || 0), 0);

  const columns = [
    { title: '#', width: 50, render: (_, __, i) => i + 1 },
    { title: 'Studio', dataIndex: 'studio', width: 250, render: (v, _, i) => <span><HomeOutlined style={{ marginRight: 6, color: studioColors[i % studioColors.length] }} /><Text strong>{v}</Text></span> },
    { title: 'Total Songs', dataIndex: 'total_songs', width: 120, sorter: (a, b) => a.total_songs - b.total_songs, defaultSortOrder: 'descend', render: v => <Tag color="blue" style={{ fontSize: 14 }}>{v}</Tag> },
    { title: 'Distributed', dataIndex: 'distributed', width: 120, render: v => <Tag color="green">{v}</Tag> },
    { title: 'Pending', dataIndex: 'pending', width: 120, render: v => parseInt(v) > 0 ? <Tag color="orange">{v}</Tag> : <Tag color="green">0</Tag> },
    { title: 'Progress', width: 180, render: (_, r) => { const pct = r.total_songs > 0 ? Math.round((r.distributed / r.total_songs) * 100) : 0; return <Progress percent={pct} status={pct >= 100 ? 'success' : 'active'} />; } },
    { title: 'Share', width: 100, render: (_, r) => { const pct = totalSongs > 0 ? Math.round((r.total_songs / totalSongs) * 100) : 0; return <Tag color="geekblue">{pct}%</Tag>; } }
  ];

  const handlePrint = () => {
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><title>Studio Report</title>
      <style>body{font-family:Arial;padding:30px}.header{background:linear-gradient(135deg,#667eea,#764ba2);color:#fff;padding:20px 30px;border-radius:12px;margin-bottom:20px}
      .header h1{margin:0;font-size:22px}table{width:100%;border-collapse:collapse;margin:16px 0}td,th{border:1px solid #e8e8e8;padding:8px 12px;text-align:left;font-size:13px}th{background:#f0f5ff}
      .footer{text-align:center;color:#999;font-size:12px;margin-top:30px;border-top:1px solid #eee;padding-top:10px}</style></head><body>
      <div class="header"><h1>Studio-wise Report</h1><p>Bainsla Music | ${dayjs().format('DD MMM YYYY')}</p></div>
      <p>Studios: <b>${studios.length}</b> | Songs: <b>${totalSongs}</b> | Distributed: <b>${totalDist}</b> | Pending: <b>${totalPending}</b></p>
      <table><tr><th>#</th><th>Studio</th><th>Total Songs</th><th>Distributed</th><th>Pending</th><th>Progress</th></tr>
      ${studios.map((s, i) => `<tr><td>${i+1}</td><td>${s.studio}</td><td>${s.total_songs}</td><td>${s.distributed}</td><td>${s.pending}</td><td>${s.total_songs > 0 ? Math.round((s.distributed / s.total_songs) * 100) : 0}%</td></tr>`).join('')}
      </table><div class="footer">Bainsla Music Data Dashboard | ${dayjs().format('DD MMM YYYY, hh:mm A')}</div></body></html>`);
    w.document.close(); setTimeout(() => w.print(), 500);
  };

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <DatePicker picker="month" placeholder="Filter by Month" value={monthFilter}
            onChange={v => setMonthFilter(v)} allowClear style={{ width: 200 }} />
          {monthFilter && <Text type="secondary" style={{ marginLeft: 8 }}>{monthFilter.format('MMMM YYYY')}</Text>}
        </Col>
        <Col>
          <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>Download PDF</Button>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}><Card size="small"><Statistic title="Studios" value={studios.length} prefix={<HomeOutlined />} valueStyle={{ color: '#667eea' }} /></Card></Col>
        <Col xs={12} sm={6}><Card size="small"><Statistic title="Songs" value={totalSongs} prefix={<SoundOutlined />} valueStyle={{ color: '#1890ff' }} /></Card></Col>
        <Col xs={12} sm={6}><Card size="small"><Statistic title="Distributed" value={totalDist} prefix={<CheckCircleOutlined />} valueStyle={{ color: '#52c41a' }} /></Card></Col>
        <Col xs={12} sm={6}><Card size="small"><Statistic title="Pending" value={totalPending} valueStyle={{ color: '#faad14' }} /></Card></Col>
      </Row>

      {loading ? <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: 40 }} /> : (
        <>
          <Card size="small" style={{ marginBottom: 16 }}>
            <Title level={5}>Studio Comparison</Title>
            {studios.map((s, i) => {
              const maxSongs = Math.max(...studios.map(st => parseInt(st.total_songs || 0)), 1);
              const barWidth = (parseInt(s.total_songs || 0) / maxSongs) * 100;
              const distWidth = (parseInt(s.distributed || 0) / maxSongs) * 100;
              return (
                <div key={i} style={{ marginBottom: 12 }}>
                  <Text style={{ fontSize: 13 }}>{s.studio}</Text>
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ background: '#f0f0f0', borderRadius: 4, height: 20, position: 'relative' }}>
                        <div style={{ width: `${barWidth}%`, background: studioColors[i % studioColors.length], height: 20, borderRadius: 4, opacity: 0.3, position: 'absolute' }} />
                        <div style={{ width: `${distWidth}%`, background: studioColors[i % studioColors.length], height: 20, borderRadius: 4, position: 'absolute' }} />
                      </div>
                    </div>
                    <Text style={{ fontSize: 12, minWidth: 80 }}>{s.distributed}/{s.total_songs} songs</Text>
                  </div>
                </div>
              );
            })}
            <div style={{ marginTop: 8, fontSize: 11 }}>
              <span style={{ display: 'inline-block', width: 12, height: 12, background: '#667eea', borderRadius: 2, marginRight: 4 }} /> Distributed
              <span style={{ display: 'inline-block', width: 12, height: 12, background: '#667eea', borderRadius: 2, marginLeft: 16, marginRight: 4, opacity: 0.3 }} /> Total Songs
            </div>
          </Card>
          <Table columns={columns} dataSource={studios} rowKey="studio" size="small" pagination={false} scroll={{ x: 800 }} />
        </>
      )}
    </div>
  );
}

function PaymentsTab() {
  const [data, setData] = useState({ payments: [], totals: [], studios: [] });
  const [loading, setLoading] = useState(false);
  const [studioFilter, setStudioFilter] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [addModal, setAddModal] = useState(false);
  const [form] = Form.useForm();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (studioFilter) params.studio = studioFilter;
      if (dateRange?.[0] && dateRange?.[1]) {
        params.from = dateRange[0].format('YYYY-MM-DD');
        params.to = dateRange[1].format('YYYY-MM-DD');
      }
      const res = await api.get('/performance/studio-payments', { params });
      setData(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [studioFilter, dateRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAdd = async (values) => {
    try {
      await api.post('/performance/studio-payments', { ...values, payment_date: values.payment_date?.format('YYYY-MM-DD') || dayjs().format('YYYY-MM-DD') });
      message.success('Payment added & Expense created!');
      setAddModal(false);
      form.resetFields();
      fetchData();
    } catch (err) { message.error('Failed to add payment'); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/performance/studio-payments/${id}`); message.success('Deleted!'); fetchData(); } catch (err) { message.error('Failed'); }
  };

  const totalPaid = (data.totals || []).reduce((s, t) => s + parseFloat(t.total_paid || 0), 0);

  const paymentColumns = [
    { title: '#', width: 50, render: (_, __, i) => i + 1 },
    { title: 'Studio', dataIndex: 'studio_name', width: 200, render: v => <Text strong><HomeOutlined style={{ marginRight: 4 }} />{v}</Text> },
    { title: 'Amount', dataIndex: 'amount', width: 120, render: v => <Text strong style={{ color: '#ff4d4f' }}>₹{Number(v).toLocaleString()}</Text> },
    { title: 'Date', dataIndex: 'payment_date', width: 120, render: v => dayjs(v).format('DD MMM YYYY') },
    { title: 'Songs', dataIndex: 'songs_count', width: 80, render: v => v > 0 ? <Tag color="blue">{v}</Tag> : '-' },
    { title: 'Payment For', dataIndex: 'payment_for', ellipsis: true },
    { title: 'Notes', dataIndex: 'notes', ellipsis: true },
    { title: 'Added By', dataIndex: 'created_by_name', width: 120 },
    { title: '', width: 40, render: (_, r) => <Popconfirm title="Delete?" onConfirm={() => handleDelete(r.id)}><DeleteOutlined style={{ color: 'red', cursor: 'pointer' }} /></Popconfirm> }
  ];

  const totalColumns = [
    { title: 'Studio', dataIndex: 'studio_name', render: v => <Text strong><HomeOutlined style={{ marginRight: 4 }} />{v}</Text> },
    { title: 'Total Paid', dataIndex: 'total_paid', render: v => <Text strong style={{ color: '#ff4d4f' }}>₹{Number(v).toLocaleString()}</Text> },
    { title: 'Payments', dataIndex: 'payment_count', render: v => <Tag color="blue">{v}</Tag> },
    { title: 'Songs Paid', dataIndex: 'total_songs_paid', render: v => <Tag color="green">{v || 0}</Tag> },
  ];

  const handlePrint = () => {
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><title>Studio Payments</title>
      <style>body{font-family:Arial;padding:30px}.header{background:linear-gradient(135deg,#667eea,#764ba2);color:#fff;padding:20px 30px;border-radius:12px;margin-bottom:20px}
      .header h1{margin:0;font-size:22px}table{width:100%;border-collapse:collapse;margin:16px 0}td,th{border:1px solid #e8e8e8;padding:8px 12px;text-align:left;font-size:13px}th{background:#f0f5ff}
      .footer{text-align:center;color:#999;font-size:12px;margin-top:30px;border-top:1px solid #eee;padding-top:10px}</style></head><body>
      <div class="header"><h1>Studio Payments Report</h1><p>Bainsla Music | ${dayjs().format('DD MMM YYYY')}</p></div>
      <p>Total Paid: <b style="color:#ff4d4f">₹${totalPaid.toLocaleString()}</b> | Studios: ${(data.totals || []).length}</p>
      <table><tr><th>Studio</th><th>Total Paid</th><th>Payments</th><th>Songs</th></tr>
      ${(data.totals || []).map(t => `<tr><td>${t.studio_name}</td><td>₹${Number(t.total_paid).toLocaleString()}</td><td>${t.payment_count}</td><td>${t.total_songs_paid || 0}</td></tr>`).join('')}
      </table>
      <h3>All Payments</h3>
      <table><tr><th>#</th><th>Studio</th><th>Amount</th><th>Date</th><th>Songs</th><th>For</th></tr>
      ${(data.payments || []).map((p, i) => `<tr><td>${i+1}</td><td>${p.studio_name}</td><td>₹${Number(p.amount).toLocaleString()}</td><td>${dayjs(p.payment_date).format('DD MMM YYYY')}</td><td>${p.songs_count || '-'}</td><td>${p.payment_for || '-'}</td></tr>`).join('')}
      </table><div class="footer">Bainsla Music Data Dashboard | ${dayjs().format('DD MMM YYYY, hh:mm A')}</div></body></html>`);
    w.document.close(); setTimeout(() => w.print(), 500);
  };

  return (
    <div>
      <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 12 }}>
        <Col xs={24} sm={6}>
          <Select placeholder="All Studios" allowClear style={{ width: '100%' }} value={studioFilter || undefined}
            onChange={v => setStudioFilter(v || '')} options={(data.studios || []).map(s => ({ value: s, label: s }))} />
        </Col>
        <Col xs={24} sm={8}>
          <RangePicker format="DD/MM/YYYY" placeholder={['From Date', 'To Date']} value={dateRange}
            onChange={dates => setDateRange(dates)} allowClear style={{ width: '100%' }} />
        </Col>
        <Col xs={24} sm={10}>
          <Row gutter={8} justify="end">
            <Col><Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModal(true)}>Add Payment</Button></Col>
            <Col><Button icon={<PrinterOutlined />} onClick={handlePrint}>PDF</Button></Col>
          </Row>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={8}><Card size="small"><Statistic title="Total Paid" value={totalPaid} prefix="₹" valueStyle={{ color: '#ff4d4f' }} /></Card></Col>
        <Col xs={8}><Card size="small"><Statistic title="Studios" value={(data.totals || []).length} prefix={<HomeOutlined />} valueStyle={{ color: '#667eea' }} /></Card></Col>
        <Col xs={8}><Card size="small"><Statistic title="Payments" value={(data.payments || []).length} prefix={<CheckCircleOutlined />} valueStyle={{ color: '#52c41a' }} /></Card></Col>
      </Row>

      {loading ? <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: 40 }} /> : (
        <>
          {(data.totals || []).length > 0 && (
            <Card title="Studio-wise Summary" size="small" style={{ marginBottom: 16 }}>
              <Table columns={totalColumns} dataSource={data.totals} rowKey="studio_name" size="small" pagination={false} />
            </Card>
          )}
          <Card title="All Payments" size="small">
            <Table columns={paymentColumns} dataSource={data.payments || []} rowKey="id" size="small" pagination={{ pageSize: 20 }} scroll={{ x: 900 }} />
          </Card>
        </>
      )}

      <Modal title="Add Studio Payment" open={addModal} onCancel={() => setAddModal(false)} footer={null}>
        <Form form={form} onFinish={handleAdd} layout="vertical">
          <Form.Item name="studio_name" label="Studio" rules={[{ required: true }]}>
            <Select placeholder="Select or type studio" showSearch allowClear options={(data.studios || []).map(s => ({ value: s, label: s }))} />
          </Form.Item>
          <Form.Item name="amount" label="Amount (₹)" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} min={1} placeholder="Amount" />
          </Form.Item>
          <Form.Item name="payment_date" label="Payment Date" initialValue={dayjs()}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="payment_for" label="Payment For">
            <Input placeholder="e.g. 5 songs recording, Album XYZ" />
          </Form.Item>
          <Form.Item name="songs_count" label="Songs Count">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>Auto-adds to Expenses as "Studio Payment"</Text>
          <Button type="primary" htmlType="submit" block>Add Payment</Button>
        </Form>
      </Modal>
    </div>
  );
}

function AssignStudioTab() {
  const [songs, setSongs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [studioFilter, setStudioFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [studios, setStudios] = useState([]);
  const debounceRef = useRef(null);

  const fetchSongs = useCallback(async (pg, search, studio, month) => {
    setLoading(true);
    try {
      const params = { page: pg, limit: 50 };
      if (search) params.search = search;
      if (studio) params.studio = studio;
      if (month) {
        params.from = month.startOf('month').format('YYYY-MM-DD');
        params.to = month.endOf('month').format('YYYY-MM-DD');
      }
      const res = await api.get('/songs', { params });
      const d = res.data;
      setSongs(d?.songs || (Array.isArray(d) ? d : []));
      setTotal(d?.total || 0);
      if (d?.studios) setStudios(d.studios);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchSongs(1, '', '', null); }, [fetchSongs]);

  const applyFilters = (search, studio, month, pg) => {
    setPage(pg || 1);
    fetchSongs(pg || 1, search, studio, month);
  };

  const assignStudio = async (songId, studio) => {
    try {
      await api.put(`/songs/${songId}/studio`, { studio: studio || '' });
      message.success('Studio updated! Song Metadata & Song Library synced.');
      setSongs(prev => prev.map(s => s.id === songId ? { ...s, studio } : s));
    } catch (err) { message.error('Failed to update'); }
  };

  const withStudio = songs.filter(s => s.studio);
  const withoutStudio = songs.filter(s => !s.studio);

  const columns = [
    { title: '#', width: 50, render: (_, __, i) => (page - 1) * 50 + i + 1 },
    { title: 'Title', dataIndex: 'title', ellipsis: true, render: v => <Text strong>{v}</Text> },
    { title: 'Singer', dataIndex: 'singer', width: 140, render: v => v || '-' },
    { title: 'Date Added', dataIndex: 'created_at', width: 120, render: v => v ? dayjs(v).format('DD MMM YYYY') : '-', sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at) },
    { title: 'Status', dataIndex: 'distribution_status', width: 100, render: v => v === 'distributed' ? <Tag color="green">Distributed</Tag> : <Tag color="orange">Pending</Tag> },
    { title: 'Current Studio', dataIndex: 'studio', width: 180, render: v => v ? <Tag color="blue">{v}</Tag> : <Tag color="default">Not Set</Tag> },
    {
      title: 'Assign Studio', width: 220,
      render: (_, r) => (
        <Select
          placeholder="Select studio"
          value={r.studio || undefined}
          allowClear
          showSearch
          style={{ width: '100%' }}
          onChange={v => assignStudio(r.id, v)}
          options={studios.map(s => ({ value: s, label: s }))}
        />
      )
    }
  ];

  return (
    <div>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>All songs shown. Assign a studio — changes reflect in Song Metadata & Song Library instantly.</Text>

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Input.Search placeholder="Type to search title, singer..." allowClear
            value={searchText}
            onChange={e => { const v = e.target.value; setSearchText(v); if (debounceRef.current) clearTimeout(debounceRef.current); debounceRef.current = setTimeout(() => applyFilters(v, studioFilter, monthFilter), 300); }}
            onSearch={v => { setSearchText(v); applyFilters(v, studioFilter, monthFilter); }} />
        </Col>
        <Col xs={12} sm={5}>
          <Select placeholder="All Studios" allowClear style={{ width: '100%' }}
            value={studioFilter || undefined}
            onChange={v => { setStudioFilter(v || ''); applyFilters(searchText, v || '', monthFilter); }}
            options={studios.map(s => ({ value: s, label: s }))} />
        </Col>
        <Col xs={12} sm={5}>
          <DatePicker picker="month" placeholder="Filter by Month" style={{ width: '100%' }}
            value={monthFilter}
            onChange={v => { setMonthFilter(v); applyFilters(searchText, studioFilter, v); }} />
        </Col>
        <Col xs={24} sm={6}>
          <Row gutter={8}>
            <Col><Statistic title="Showing" value={songs.length} valueStyle={{ fontSize: 16, color: '#1890ff' }} /></Col>
            <Col><Statistic title="With Studio" value={withStudio.length} valueStyle={{ fontSize: 16, color: '#52c41a' }} /></Col>
            <Col><Statistic title="No Studio" value={withoutStudio.length} valueStyle={{ fontSize: 16, color: '#faad14' }} /></Col>
          </Row>
        </Col>
      </Row>

      <Table columns={columns} dataSource={songs} rowKey="id" size="small" loading={loading}
        pagination={{ current: page, total, pageSize: 50, onChange: pg => { setPage(pg); fetchSongs(pg, searchText, studioFilter, monthFilter); }, showTotal: t => `Total ${t} songs` }}
        scroll={{ x: 900 }} />
    </div>
  );
}

function RecordingLogTab() {
  const [data, setData] = useState({ logs: [], summary: [], studios: [] });
  const [loading, setLoading] = useState(false);
  const [studioFilter, setStudioFilter] = useState('');
  const [yearFilter, setYearFilter] = useState(dayjs());
  const [addModal, setAddModal] = useState(false);
  const [form] = Form.useForm();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (studioFilter) params.studio = studioFilter;
      if (yearFilter) params.year = yearFilter.format('YYYY');
      const res = await api.get('/performance/studio-recording-log', { params });
      setData(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [studioFilter, yearFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAdd = async (values) => {
    try {
      await api.post('/performance/studio-recording-log', {
        studio_name: values.studio_name,
        month: values.month?.format('YYYY-MM') || dayjs().format('YYYY-MM'),
        songs_recorded: values.songs_recorded || 0,
        songs_uploaded: values.songs_uploaded || 0,
        notes: values.notes || ''
      });
      message.success('Recording log saved!');
      setAddModal(false);
      form.resetFields();
      fetchData();
    } catch (err) { message.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/performance/studio-recording-log/${id}`); message.success('Deleted!'); fetchData(); } catch (err) { message.error('Failed'); }
  };

  const totalRecorded = (data.summary || []).reduce((s, r) => s + parseInt(r.total_recorded || 0), 0);
  const totalUploaded = (data.summary || []).reduce((s, r) => s + parseInt(r.total_uploaded || 0), 0);
  const totalPending = totalRecorded - totalUploaded;

  const monthNames = { '01': 'Jan', '02': 'Feb', '03': 'Mar', '04': 'Apr', '05': 'May', '06': 'Jun', '07': 'Jul', '08': 'Aug', '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Dec' };
  const formatMonth = (m) => { const parts = m.split('-'); return `${monthNames[parts[1]] || parts[1]} ${parts[0]}`; };

  const logColumns = [
    { title: '#', width: 50, render: (_, __, i) => i + 1 },
    { title: 'Studio', dataIndex: 'studio_name', width: 200, render: v => <Text strong><HomeOutlined style={{ marginRight: 4 }} />{v}</Text> },
    { title: 'Month', dataIndex: 'month', width: 120, render: v => <Tag color="blue">{formatMonth(v)}</Tag> },
    { title: 'Recorded', dataIndex: 'songs_recorded', width: 100, render: v => <Tag color="purple" style={{ fontSize: 14 }}>{v}</Tag> },
    { title: 'Uploaded', dataIndex: 'songs_uploaded', width: 100, render: v => <Tag color="green" style={{ fontSize: 14 }}>{v}</Tag> },
    { title: 'Pending', width: 100, render: (_, r) => { const p = (r.songs_recorded || 0) - (r.songs_uploaded || 0); return p > 0 ? <Tag color="orange" style={{ fontSize: 14 }}>{p}</Tag> : <Tag color="green">0</Tag>; } },
    { title: 'Progress', width: 150, render: (_, r) => { const pct = r.songs_recorded > 0 ? Math.round((r.songs_uploaded / r.songs_recorded) * 100) : 0; return <Progress percent={pct} size="small" status={pct >= 100 ? 'success' : 'active'} />; } },
    { title: 'Notes', dataIndex: 'notes', ellipsis: true },
    { title: 'Added By', dataIndex: 'created_by_name', width: 120 },
    { title: '', width: 40, render: (_, r) => <Popconfirm title="Delete?" onConfirm={() => handleDelete(r.id)}><DeleteOutlined style={{ color: 'red', cursor: 'pointer' }} /></Popconfirm> }
  ];

  const summaryColumns = [
    { title: 'Studio', dataIndex: 'studio_name', render: v => <Text strong><HomeOutlined style={{ marginRight: 4 }} />{v}</Text> },
    { title: 'Total Recorded', dataIndex: 'total_recorded', render: v => <Tag color="purple" style={{ fontSize: 14 }}>{v}</Tag> },
    { title: 'Total Uploaded', dataIndex: 'total_uploaded', render: v => <Tag color="green" style={{ fontSize: 14 }}>{v}</Tag> },
    { title: 'Pending', dataIndex: 'total_pending', render: v => parseInt(v) > 0 ? <Tag color="orange" style={{ fontSize: 14 }}>{v}</Tag> : <Tag color="green">0</Tag> },
    { title: 'Progress', render: (_, r) => { const pct = r.total_recorded > 0 ? Math.round((r.total_uploaded / r.total_recorded) * 100) : 0; return <Progress percent={pct} size="small" status={pct >= 100 ? 'success' : 'active'} />; } },
  ];

  const handlePrint = () => {
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><title>Studio Recording Log</title>
      <style>body{font-family:Arial;padding:30px}.header{background:linear-gradient(135deg,#667eea,#764ba2);color:#fff;padding:20px 30px;border-radius:12px;margin-bottom:20px}
      .header h1{margin:0;font-size:22px}table{width:100%;border-collapse:collapse;margin:16px 0}td,th{border:1px solid #e8e8e8;padding:8px 12px;text-align:left;font-size:13px}th{background:#f0f5ff}
      .footer{text-align:center;color:#999;font-size:12px;margin-top:30px;border-top:1px solid #eee;padding-top:10px}</style></head><body>
      <div class="header"><h1>Studio Recording Log — ${yearFilter ? yearFilter.format('YYYY') : 'All'}</h1><p>Bainsla Music | ${dayjs().format('DD MMM YYYY')}</p></div>
      <p>Total Recorded: <b>${totalRecorded}</b> | Uploaded: <b>${totalUploaded}</b> | Pending: <b style="color:#faad14">${totalPending}</b></p>
      <h3>Studio Summary</h3>
      <table><tr><th>Studio</th><th>Recorded</th><th>Uploaded</th><th>Pending</th><th>Progress</th></tr>
      ${(data.summary || []).map(s => `<tr><td>${s.studio_name}</td><td>${s.total_recorded}</td><td>${s.total_uploaded}</td><td>${s.total_pending}</td><td>${s.total_recorded > 0 ? Math.round((s.total_uploaded / s.total_recorded) * 100) : 0}%</td></tr>`).join('')}
      </table>
      <h3>Monthly Details</h3>
      <table><tr><th>#</th><th>Studio</th><th>Month</th><th>Recorded</th><th>Uploaded</th><th>Pending</th><th>Notes</th></tr>
      ${(data.logs || []).map((l, i) => `<tr><td>${i+1}</td><td>${l.studio_name}</td><td>${formatMonth(l.month)}</td><td>${l.songs_recorded}</td><td>${l.songs_uploaded}</td><td>${(l.songs_recorded||0)-(l.songs_uploaded||0)}</td><td>${l.notes||'-'}</td></tr>`).join('')}
      </table><div class="footer">Bainsla Music Data Dashboard | ${dayjs().format('DD MMM YYYY, hh:mm A')}</div></body></html>`);
    w.document.close(); setTimeout(() => w.print(), 500);
  };

  return (
    <div>
      <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 12 }}>
        <Col xs={12} sm={6}>
          <Select placeholder="All Studios" allowClear style={{ width: '100%' }} value={studioFilter || undefined}
            onChange={v => setStudioFilter(v || '')} options={(data.studios || []).map(s => ({ value: s, label: s }))} />
        </Col>
        <Col xs={12} sm={4}>
          <DatePicker picker="year" placeholder="Year" value={yearFilter} onChange={v => setYearFilter(v)} style={{ width: '100%' }} />
        </Col>
        <Col xs={24} sm={14}>
          <Row gutter={8} justify="end">
            <Col><Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModal(true)}>Add Entry</Button></Col>
            <Col><Button icon={<PrinterOutlined />} onClick={handlePrint}>PDF</Button></Col>
          </Row>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={8}><Card size="small"><Statistic title="Total Recorded" value={totalRecorded} prefix={<SoundOutlined />} valueStyle={{ color: '#722ed1' }} /></Card></Col>
        <Col xs={8}><Card size="small"><Statistic title="Total Uploaded" value={totalUploaded} prefix={<CheckCircleOutlined />} valueStyle={{ color: '#52c41a' }} /></Card></Col>
        <Col xs={8}><Card size="small"><Statistic title="Pending" value={totalPending > 0 ? totalPending : 0} valueStyle={{ color: '#faad14' }} /></Card></Col>
      </Row>

      {loading ? <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: 40 }} /> : (
        <>
          {(data.summary || []).length > 0 && (
            <Card title="Studio-wise Summary" size="small" style={{ marginBottom: 16 }}>
              <Table columns={summaryColumns} dataSource={data.summary} rowKey="studio_name" size="small" pagination={false} />
            </Card>
          )}
          <Card title="Monthly Recording Log" size="small">
            <Table columns={logColumns} dataSource={data.logs || []} rowKey="id" size="small" pagination={{ pageSize: 20 }} scroll={{ x: 1000 }} />
          </Card>
        </>
      )}

      <Modal title="Add Recording Entry" open={addModal} onCancel={() => setAddModal(false)} footer={null}>
        <Form form={form} onFinish={handleAdd} layout="vertical">
          <Form.Item name="studio_name" label="Studio" rules={[{ required: true }]}>
            <Select placeholder="Select studio" showSearch allowClear options={(data.studios || []).map(s => ({ value: s, label: s }))} />
          </Form.Item>
          <Form.Item name="month" label="Month" rules={[{ required: true }]} initialValue={dayjs()}>
            <DatePicker picker="month" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="songs_recorded" label="Songs Recorded" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} min={0} placeholder="How many songs recorded this month?" />
          </Form.Item>
          <Form.Item name="songs_uploaded" label="Songs Uploaded (to server)" initialValue={0}>
            <InputNumber style={{ width: '100%' }} min={0} placeholder="How many uploaded so far?" />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} placeholder="Optional notes" />
          </Form.Item>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>Same studio + month entry will update existing record</Text>
          <Button type="primary" htmlType="submit" block>Save Entry</Button>
        </Form>
      </Modal>
    </div>
  );
}

function ManageStudiosTab() {
  const [studios, setStudios] = useState([]);
  const [customStudios, setCustomStudios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);

  const fetchStudios = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/performance/studios');
      setStudios(res.data?.studios || []);
      setCustomStudios(res.data?.custom || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchStudios(); }, [fetchStudios]);

  const addStudio = async () => {
    const name = newName.trim();
    if (!name) { message.warning('Studio name enter karo'); return; }
    setAdding(true);
    try {
      await api.post('/performance/studios', { name });
      message.success(`Studio "${name}" added!`);
      setNewName('');
      fetchStudios();
    } catch (e) {
      message.error(e.response?.data?.error || 'Failed to add studio');
    }
    setAdding(false);
  };

  const deleteStudio = async (id) => {
    try {
      await api.delete(`/performance/studios/${id}`);
      message.success('Studio deleted');
      fetchStudios();
    } catch (e) { message.error('Failed to delete'); }
  };

  return (
    <div>
      <Card size="small" style={{ marginBottom: 16, background: '#f0f5ff', borderLeft: '4px solid #667eea' }}
        title={<span><PlusOutlined style={{ color: '#667eea' }} /> Add New Studio</span>}>
        <Row gutter={12} align="middle">
          <Col flex="auto">
            <Input
              placeholder="New studio name (e.g. Sunrise Studio, Music Hub)"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onPressEnter={addStudio}
              size="large"
            />
          </Col>
          <Col>
            <Button type="primary" icon={<PlusOutlined />} size="large" loading={adding} onClick={addStudio}>
              Add Studio
            </Button>
          </Col>
        </Row>
      </Card>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8}><Card size="small"><Statistic title="Total Studios" value={studios.length} prefix={<HomeOutlined />} valueStyle={{ color: '#667eea' }} /></Card></Col>
        <Col xs={12} sm={8}><Card size="small"><Statistic title="Custom Added" value={customStudios.length} prefix={<PlusOutlined />} valueStyle={{ color: '#52c41a' }} /></Card></Col>
        <Col xs={24} sm={8}><Card size="small"><Statistic title="From Songs" value={studios.length - customStudios.length} prefix={<SoundOutlined />} valueStyle={{ color: '#1890ff' }} /></Card></Col>
      </Row>

      <Card title="All Studios" size="small" loading={loading}>
        <Table
          dataSource={studios.map((s, i) => ({ key: i, name: s, custom: customStudios.find(c => c.name === s) }))}
          columns={[
            { title: '#', width: 50, render: (_, __, i) => i + 1 },
            { title: 'Studio Name', dataIndex: 'name', render: (v, r) => (
              <span>
                <HomeOutlined style={{ marginRight: 6, color: studioColors[r.key % studioColors.length] }} />
                <Text strong>{v}</Text>
                {r.custom && <Tag color="green" style={{ marginLeft: 8 }}>Custom</Tag>}
              </span>
            )},
            { title: 'Source', render: (_, r) => r.custom ? <Tag color="green">Manually Added</Tag> : <Tag color="blue">From Songs</Tag> },
            { title: 'Added By', render: (_, r) => r.custom?.creator_name || '-' },
            { title: 'Action', width: 80, render: (_, r) => r.custom ? (
              <Popconfirm title="Delete this studio?" onConfirm={() => deleteStudio(r.custom.id)}>
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            ) : '-' }
          ]}
          size="small"
          pagination={false}
        />
      </Card>
    </div>
  );
}

export default function StudioReport() {
  return (
    <div>
      <Title level={3}><BarChartOutlined /> Studio Report & Payments</Title>
      <Text type="secondary">Studio-wise report, payments, recording log, and song assignment</Text>
      <Tabs defaultActiveKey="report" style={{ marginTop: 8 }} items={[
        { key: 'report', label: 'Studio Report', children: <ReportTab /> },
        { key: 'manage', label: 'Manage Studios', children: <ManageStudiosTab /> },
        { key: 'recording-log', label: 'Recording Log', children: <RecordingLogTab /> },
        { key: 'payments', label: 'Studio Payments', children: <PaymentsTab /> },
        { key: 'assign', label: 'Assign Studio to Songs', children: <AssignStudioTab /> },
      ]} />
    </div>
  );
}
